import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { COUNTRIES } from 'components/CountrySelect/countries';

import { DEFAULT_FULL_DATE_FORMAT, SLASH_FULL_DATE_FORMAT } from 'core/constants';

import { CSVImportColumns, UploadUserType } from 'utils/api/types';
import { UserFieldOption } from 'utils/api/modules/user/user.types';

import {
  CUSTOM_FIELD_SLOTS,
  IMPORT_ATTRIBUTE_KEYS,
  IMPORT_COLUMN_KEYS,
  ImportUnitOption,
  ImportVocabulary,
  customFieldKey,
} from './importVocabulary';

dayjs.extend(customParseFormat);

export interface ResolvedImportFields {
  manager?: string;
  jobTitle?: string;
  employeeId?: string;
  startDate?: string;
  country?: string;
  department?: string;
  region?: string;
  employmentType?: string;
  site?: string;
  unit?: string;
  customFields?: { slot: number; value: string }[];
}

export interface SiteLookupResult {
  uuid?: string;
  error?: string;
}

export interface ResolvedImportRow {
  fields: ResolvedImportFields;
  error?: string;
}

const COUNTRY_CODES = new Map<string, string>();
COUNTRIES.forEach(({ code, name }) => {
  COUNTRY_CODES.set(name.toLowerCase(), code);
  COUNTRY_CODES.set(code.toLowerCase(), code);
});

/** Papaparse puts an array under `__parsed_extra` for cells past the header row, so a scan over
 * `Object.values` cannot assume every value is a string. */
const cell = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const activeOptionLookup = (options: UserFieldOption[]): Map<string, string> =>
  new Map(options.filter((option) => option.isActive).map((option) => [option.name.trim().toLowerCase(), option.uuid]));

const unitKey = (segments: string[]): string =>
  segments
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean)
    .join('/');

const unitLookup = (options: ImportUnitOption[]): Map<string, string | null> => {
  const byPath = new Map<string, string | null>();
  options.forEach(({ path, uuid }) => {
    for (let start = path.length - 1; start >= 0; start -= 1) {
      const key = unitKey(path.slice(start));
      byPath.set(key, byPath.has(key) ? null : uuid);
    }
  });
  return byPath;
};

export const importColumnLabels = (vocabulary: ImportVocabulary): Record<string, string> => {
  const labels: Record<string, string> = {
    jobTitle: CSVImportColumns.jobTitle,
    employeeId: CSVImportColumns.employeeId,
    startDate: CSVImportColumns.startDate,
    country: CSVImportColumns.country,
    site: CSVImportColumns.site,
    manager: CSVImportColumns.manager,
    unit: CSVImportColumns.unit,
    department: vocabulary.departmentLabel,
    region: vocabulary.regionLabel,
    employmentType: vocabulary.employmentTypeLabel,
  };
  vocabulary.customFields.forEach(({ slot, label }) => {
    labels[customFieldKey(slot)] = label;
  });
  return labels;
};

/** Papaparse keeps only the last of two columns mapping to one field, so report rather than apply. */
export const duplicateImportColumns = (fields: string[], vocabulary: ImportVocabulary): string[] => {
  const labels = importColumnLabels(vocabulary);
  const counts = new Map<string, number>();
  fields.forEach((field) => counts.set(field, (counts.get(field) ?? 0) + 1));
  return IMPORT_COLUMN_KEYS.filter((key) => (counts.get(key) ?? 0) > 1).map((key) => labels[key] ?? key);
};

/** Papaparse emits a row of empty cells for a sheet's trailing newline; it must not reach the import count. */
export const isBlankImportRow = (row: UploadUserType): boolean =>
  Object.values(row).every((value) => cell(value) === '');

export const siteNamesInSheet = (rows: UploadUserType[]): string[] => {
  const names = new Set<string>();
  rows.forEach((row) => {
    const value = cell(row.site);
    if (value) {
      names.add(value.toLowerCase());
    }
  });
  return [...names];
};

export const createImportRowResolver = (
  vocabulary: ImportVocabulary
): ((row: UploadUserType, sites: Map<string, SiteLookupResult>) => ResolvedImportRow) => {
  const labels = importColumnLabels(vocabulary);
  const units = unitLookup(vocabulary.unitOptions);
  const options: Record<'department' | 'region' | 'employmentType', Map<string, string>> = {
    department: activeOptionLookup(vocabulary.departmentOptions),
    region: activeOptionLookup(vocabulary.regionOptions),
    employmentType: activeOptionLookup(vocabulary.employmentTypeOptions),
  };

  return (row, sites) => {
    const fields: ResolvedImportFields = {};
    // Only the core-api-owned fields: Line manager is an email the server resolves, so neither
    // suppression nor the entitlement gates apply to it.
    const present = IMPORT_ATTRIBUTE_KEYS.filter((key) => cell(row[key]));

    if (vocabulary.suppressed && present.length) {
      return {
        fields: {},
        error: `${labels[present[0]] ?? present[0]} is not supported when importing into another account.`,
      };
    }

    const unsupported = present.find((key) => {
      if (key === 'site') {
        return !vocabulary.showSite;
      }
      if (key === 'region' || key.startsWith('customField')) {
        return !vocabulary.showObjectManager;
      }
      return false;
    });
    if (unsupported) {
      return {
        fields: {},
        error: `${labels[unsupported] ?? unsupported} is not supported for your organisation.`,
      };
    }

    const manager = cell(row.manager);
    if (manager) {
      fields.manager = manager;
    }

    const jobTitle = cell(row.jobTitle);
    if (jobTitle) {
      fields.jobTitle = jobTitle;
    }

    const employeeId = cell(row.employeeId);
    if (employeeId) {
      fields.employeeId = employeeId;
    }

    const startDate = cell(row.startDate);
    if (startDate) {
      const parsed = dayjs(startDate, [SLASH_FULL_DATE_FORMAT, DEFAULT_FULL_DATE_FORMAT], true);
      if (!parsed.isValid()) {
        return {
          fields: {},
          error: `${labels.startDate} "${startDate}" must be ${SLASH_FULL_DATE_FORMAT} or ${DEFAULT_FULL_DATE_FORMAT}.`,
        };
      }
      fields.startDate = parsed.format(DEFAULT_FULL_DATE_FORMAT);
    }

    const country = cell(row.country);
    if (country) {
      const code = COUNTRY_CODES.get(country.toLowerCase());
      if (!code) {
        return { fields: {}, error: `${labels.country} "${country}" was not recognised.` };
      }
      fields.country = code;
    }

    for (const key of ['department', 'region', 'employmentType'] as const) {
      const value = cell(row[key]);
      if (!value) {
        continue;
      }
      const uuid = options[key].get(value.toLowerCase());
      if (!uuid) {
        return { fields: {}, error: `${labels[key]} "${value}" was not found.` };
      }
      fields[key] = uuid;
    }

    const site = cell(row.site);
    if (site) {
      const lookup = sites.get(site.toLowerCase());
      if (!lookup?.uuid) {
        return { fields: {}, error: lookup?.error ?? `${labels.site} "${site}" was not found.` };
      }
      fields.site = lookup.uuid;
    }

    if (vocabulary.showUnit) {
      const unit = cell(row.unit);
      if (!unit) {
        return { fields: {}, error: `${labels.unit} is required for every person.` };
      }
      const uuid = units.get(unitKey(unit.split('/')));
      if (uuid === undefined) {
        return { fields: {}, error: `${labels.unit} "${unit}" was not found.` };
      }
      if (uuid === null) {
        return {
          fields: {},
          error: `${labels.unit} "${unit}" matches more than one unit. Name its parent too, like "North / Leeds".`,
        };
      }
      fields.unit = uuid;
    }

    const activeSlots = new Set(vocabulary.customFields.map(({ slot }) => slot));
    // A blank cell omits the slot: the API upserts a row per item, so '' would write an empty field.
    const customFields = CUSTOM_FIELD_SLOTS.filter((slot) => activeSlots.has(slot))
      .map((slot) => ({ slot, value: cell(row[customFieldKey(slot)]) }))
      .filter(({ value }) => value !== '');
    if (customFields.length) {
      fields.customFields = customFields;
    }

    return { fields };
  };
};
